import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { libraryAPI, batchesAPI, translationAPI } from '../utils/api';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { tField } from '../utils/bilingualHelper';

function ItemModal({ onClose, onSave, item = null, batches = [] }) {
  const { t } = useTranslation();
  const isEdit = !!item;
  const [form, setForm] = useState(() => {
    if (item) {
      let mappedBatchIds = [];
      if (item.batchIds && item.batchIds.length > 0) {
        mappedBatchIds = item.batchIds.map(b => typeof b === 'object' ? (b._id || b.id) : b);
      } else if (item.batchId) {
        mappedBatchIds = [typeof item.batchId === 'object' ? (item.batchId._id || item.batchId.id) : item.batchId];
      }

      let parsedAudience = item.audience || ['student'];
      if (typeof parsedAudience === 'string') {
        parsedAudience = parsedAudience.split(',').map(s => s.trim());
      }

      // Parse bilingual title
      const rawTitle = item.title;
      const titleEn = typeof rawTitle === 'object' ? (rawTitle?.en || '') : (rawTitle || '');
      const titleZh = typeof rawTitle === 'object' ? (rawTitle?.zh || '') : '';

      // Parse bilingual subject
      const rawSubject = item.subject;
      const subjectEn = typeof rawSubject === 'object' ? (rawSubject?.en || '') : (rawSubject || '');
      const subjectZh = typeof rawSubject === 'object' ? (rawSubject?.zh || '') : '';

      return {
        ...item,
        titleEn,
        titleZh,
        subjectEn,
        subjectZh,
        batchIds: mappedBatchIds,
        audience: parsedAudience
      };
    }
    return { titleEn: '', titleZh: '', type: 'video', url: '', subjectEn: '', subjectZh: '', batchIds: [], audience: ['student'] };
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [sourceType, setSourceType] = useState(() => form.type === 'video' ? 'upload' : 'link'); // 'link' or 'upload'
  const [loading, setLoad] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translated, setTranslated] = useState(!!(form.titleZh || form.subjectZh));

  const handleTranslateAll = async () => {
    if (!form.titleEn) {
      alert("Please fill in the English Title first.");
      return;
    }
    setTranslating(true);
    try {
      const texts = [form.titleEn];
      if (form.subjectEn) texts.push(form.subjectEn);
      
      const translatedTexts = [];
      for (const text of texts) {
        const data = await translationAPI.translate(text, 'en', 'zh-TW');
        translatedTexts.push(data.responseData?.translatedText || '');
        await new Promise(r => setTimeout(r, 300));
      }
      
      setForm(prev => ({
        ...prev,
        titleZh: translatedTexts[0],
        ...(form.subjectEn ? { subjectZh: translatedTexts[1] } : {})
      }));
      setTranslated(true);
    } catch (err) {
      console.error(err);
      alert('Translation failed. Please try again or fill manually.');
    } finally {
      setTranslating(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.titleEn) return alert('Please provide an English title.');
    if (!form.titleZh) return alert('Please provide a Chinese title (中文标题).');
    setLoad(true);
    const titlePayload = { en: form.titleEn.trim(), zh: form.titleZh.trim() };
    const subjectPayload = form.subjectEn || form.subjectZh
      ? { en: form.subjectEn.trim(), zh: form.subjectZh.trim() }
      : '';
    try {
      if (isEdit) {
        await libraryAPI.updateItem(item._id || item.id, { ...form, title: titlePayload, subject: subjectPayload });
      } else {
        if (sourceType === 'upload' || form.type === 'video') {
          if (!uploadFile) return alert('Please select a file to upload.');
          const ext = uploadFile.name.split('.').pop().toLowerCase();
          if (form.type === 'video' && ext !== 'mp4') {
            setLoad(false);
            return alert('For Video type, only MP4 files are allowed.');
          } else if (form.type === 'image' && !['jpeg', 'jpg', 'png'].includes(ext)) {
            setLoad(false);
            return alert('For Image type, only JPEG, JPG, or PNG files are allowed.');
          } else if (form.type === 'document' && ext !== 'pdf') {
            setLoad(false);
            return alert('For Document type, only PDF files are allowed.');
          }
          if (form.type === 'video') {
            const bunnyRes = await libraryAPI.uploadVideo(uploadFile, form.titleEn);
            const payload = {
              ...form,
              title: titlePayload,
              subject: subjectPayload,
              url: bunnyRes.data.playbackUrl,
              videoGuid: bunnyRes.data.videoGuid,
              batchId: JSON.stringify(form.batchIds)
            };
            await libraryAPI.submitItem(payload);
          } else {
            const formData = new FormData();
            formData.append('title', JSON.stringify(titlePayload));
            formData.append('type', form.type);
            formData.append('subject', JSON.stringify(subjectPayload));
            formData.append('batchId', JSON.stringify(form.batchIds));
            if (form.audience && form.audience.length > 0) {
              form.audience.forEach(a => formData.append('audience', a));
            } else {
              formData.append('audience', 'student');
            }
            formData.append('file', uploadFile);
            await libraryAPI.submitItem(formData);
          }
        } else {
          await libraryAPI.submitItem({ ...form, title: titlePayload, subject: subjectPayload });
        }
      }
      onSave();
      onClose();
    } catch (err) { 
      alert(err.response?.data?.message || 'Failed to save item.'); 
    } finally { setLoad(false); }
  };

  return (
    <div className="dash-modal-overlay">
      <div className="dash-modal" style={{ maxWidth: 480 }}>
        <div className="dash-modal-header">
          <div className="dash-modal-title">{isEdit ? t('library.editItem') : t('library.newItem')}</div>
          <button className="dash-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="dash-modal-body">
          {/* Bilingual Title */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#666', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>📝 {t('library.resTitle', 'Resource Title')} <span style={{ color: '#e05252' }}>*</span></div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>🇬🇧 English</div>
                <input className="form-input-style" placeholder="e.g. Advanced Footwork Guide" value={form.titleEn} onChange={e => { setForm(p=>({...p, titleEn: e.target.value})); setTranslated(false); }} />
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>🇨🇳 中文</div>
              <input className="form-input-style" placeholder="例如：高级步法指南" value={form.titleZh} onChange={e => { setForm(p=>({...p, titleZh: e.target.value})); setTranslated(false); }} />
            </div>
          </div>

          {/* ── One-click Translate All button ── */}
          <div style={{ background: translated ? 'rgba(76,175,134,0.08)' : 'rgba(123,29,58,0.06)', border: `1px solid ${translated ? '#4caf86' : 'rgba(123,29,58,0.2)'}`, borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              {translated ? (
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#4caf86', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                </div>
              ) : (
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(123,29,58,0.1)', color: 'var(--wine)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"></path></svg>
                </div>
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: translated ? '#2d6a4f' : '#111', fontSize: 14, marginBottom: 2 }}>{translated ? 'Content Translated' : 'Auto-Translate'}</div>
                <div style={{ fontSize: 12, color: '#888' }}>Fill English fields above, then click to auto-translate.</div>
              </div>
              <button 
                onClick={handleTranslateAll} 
                disabled={translating}
                style={{ background: translated ? '#4caf86' : 'var(--wine)', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontWeight: 700, fontSize: 13, cursor: translating ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', opacity: translating ? 0.7 : 1, minWidth: 120 }}
              >
                {translating ? '⏳ Translating...' : translated ? '🔄 Re-translate' : '✨ Translate All'}
              </button>
          </div>

          <div className="form-row-2">
            <div className="form-group">
              <label className="form-label-style">{t('library.resType')}</label>
              <select className="form-input-style" value={form.type} onChange={e => {
                const val = e.target.value;
                setForm(p=>({...p,type:val}));
                if (val === 'video') setSourceType('upload');
                setUploadFile(null);
              }}>
                <option value="video">{t('library.videoCourse')}</option>
                <option value="document">{t('library.pdfGuide')}</option>
                <option value="image">{t('library.image')}</option>
              </select>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label-style">{t('library.subject')}</label>
              <div style={{ display: 'flex', gap: 6, flexDirection: 'column' }}>
                <input className="form-input-style" placeholder="🇬🇧 Subject (EN)" value={form.subjectEn} onChange={e => { setForm(p=>({...p, subjectEn: e.target.value})); setTranslated(false); }} />
                <input className="form-input-style" placeholder="🇨🇳 科目 (ZH)" value={form.subjectZh} onChange={e => { setForm(p=>({...p, subjectZh: e.target.value})); setTranslated(false); }} />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label-style">{t('library.targetBatches')}</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {batches.length === 0 ? (
                <div style={{ fontSize: 12, color: '#888' }}>{t('library.noBatches')}</div>
              ) : (
                batches.map(b => {
                  const isSelected = form.batchIds.includes(b._id || b.id);
                  return (
                    <div 
                      key={b._id || b.id} 
                      onClick={() => {
                        const id = b._id || b.id;
                        setForm(p => ({
                          ...p,
                          batchIds: isSelected ? p.batchIds.filter(x => x !== id) : [...p.batchIds, id]
                        }));
                      }}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 12,
                        fontSize: 12,
                        cursor: 'pointer',
                        border: `1px solid ${isSelected ? '#e05252' : '#ccc'}`,
                        background: isSelected ? 'rgba(224, 82, 82, 0.1)' : '#fff',
                        color: isSelected ? '#e05252' : '#333',
                        fontWeight: isSelected ? 600 : 400
                      }}
                    >
                      {tField(b.name)}
                    </div>
                  );
                })
              )}
            </div>
            {form.batchIds.length === 0 && (
              <div style={{ fontSize: 11, color: '#e69900', marginTop: 6 }}>
                ⚠️ No batches selected. This resource will not be limited by batch.
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label-style">{t('library.targetAudience', 'Target Audience')}</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {['student', 'parent', 'tutor', 'global'].map(role => {
                const isSelected = form.audience.includes(role);
                return (
                  <div 
                    key={role} 
                    onClick={() => {
                      if (role === 'global') {
                        setForm(p => ({ ...p, audience: isSelected ? [] : ['global'] }));
                      } else {
                        setForm(p => {
                          const withoutGlobal = p.audience.filter(x => x !== 'global');
                          return {
                            ...p,
                            audience: isSelected 
                              ? withoutGlobal.filter(x => x !== role) 
                              : [...withoutGlobal, role]
                          };
                        });
                      }
                    }}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 12,
                      fontSize: 12,
                      cursor: 'pointer',
                      border: `1px solid ${isSelected ? '#e05252' : '#ccc'}`,
                      background: isSelected ? 'rgba(224, 82, 82, 0.1)' : '#fff',
                      color: isSelected ? '#e05252' : '#333',
                      textTransform: 'capitalize',
                      fontWeight: isSelected ? 600 : 400
                    }}
                  >
                    {role}
                  </div>
                );
              })}
            </div>
            {form.audience.length === 0 && (
              <div style={{ fontSize: 11, color: '#e69900', marginTop: 6 }}>
                {t('library.noAudienceWarning', '⚠️ No audience selected. It will default to Student.')}
              </div>
            )}
          </div>

          {!isEdit && form.type !== 'video' && (
            <div className="form-group">
              <label className="form-label-style">{t('library.submissionMode', 'Submission Mode')}</label>
              <div style={{ display: 'flex', gap: 10 }}>
                <button 
                  type="button"
                  className={`btn btn-xs ${sourceType === 'link' ? 'btn-primary' : 'btn-outline'}`}
                  style={{ flex: 1 }}
                  onClick={() => setSourceType('link')}
                >{t('library.externalLink', 'External Link')}</button>
                <button 
                  type="button"
                  className={`btn btn-xs ${sourceType === 'upload' ? 'btn-primary' : 'btn-outline'}`}
                  style={{ flex: 1 }}
                  onClick={() => setSourceType('upload')}
                >{t('library.directUpload', 'Direct Upload')}</button>
              </div>
            </div>
          )}

          {(sourceType === 'upload' || form.type === 'video') && !isEdit && (
            <div className="form-group" style={{ padding: '15px', background: '#fafafa', borderRadius: 8, border: '1px dashed #ccc' }}>
              <label className="form-label-style">{t('library.selectFile', 'Select File')} ({form.type === 'video' ? 'MP4' : form.type === 'image' ? 'JPEG/PNG' : 'PDF'})</label>
              <input 
                type="file" 
                accept={form.type === 'video' ? '.mp4,video/mp4' : form.type === 'image' ? '.jpg,.jpeg,.png,image/jpeg,image/png' : '.pdf,application/pdf'} 
                onChange={e => setUploadFile(e.target.files[0])} 
              />
              <div style={{ fontSize: 10, marginTop: 8, color: '#666' }}>{form.type === 'video' ? 'Only MP4 files allowed' : form.type === 'image' ? 'Only JPEG, JPG, PNG files allowed' : 'Only PDF files allowed'} (Max File Size: 50MB)</div>
            </div>
          )}

          {sourceType === 'link' && form.type !== 'video' && !isEdit && (
            <div className="form-group">
              <label className="form-label-style">{form.type === 'image' ? 'Image URL' : 'Document Download Link'}</label>
              <input 
                className="form-input-style" 
                placeholder={form.type === 'image' ? 'https://example.com/image.jpg' : 'https://drive.google.com/...'}
                value={form.url} 
                onChange={e => setForm(p=>({...p,url:e.target.value}))} 
              />
            </div>
          )}
        </div>
        <div className="dash-modal-footer">
          <button className="btn btn-outline btn-sm" onClick={onClose}>{t('library.cancel')}</button>
          <button className="btn btn-primary btn-sm" onClick={handleSubmit} disabled={loading}>
            {loading ? t('library.processing', 'Processing...') : t('library.deployResource', 'Deploy Resource')}
          </button>
        </div>
      </div>
    </div>
  );
}

function VideoTheaterModal({ url, title, videoGuid, onClose }) {
  // Smart conversion for YouTube
  let embedUrl = url;
  const isYoutube = url.includes('youtube.com') || url.includes('youtu.be');
  
  if (isYoutube) {
    const vId = url.includes('v=') ? url.split('v=')[1].split('&')[0] : url.split('/').pop();
    embedUrl = `https://www.youtube.com/embed/${vId}?autoplay=1`;
  }

  const bunnyLibraryId = process.env.REACT_APP_BUNNY_LIBRARY_ID || '709820';

  return (
    <div className="theater-overlay" onClick={onClose}>
      <div className="theater-content" onClick={e => e.stopPropagation()}>
        <div className="theater-header">
          <div className="theater-title">{title}</div>
          <button className="theater-close" onClick={onClose}>✕</button>
        </div>
        <div className="theater-body">
          {videoGuid ? (
            <iframe 
              src={`https://iframe.mediadelivery.net/embed/${bunnyLibraryId}/${videoGuid}?autoplay=true`}
              title={title}
              className="theater-player"
              frameBorder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
              allowFullScreen
            />
          ) : isYoutube ? (
            <iframe 
              src={embedUrl} 
              title={title}
              className="theater-player"
              frameBorder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
              allowFullScreen
            />
          ) : (
            <video src={url} controls autoPlay className="theater-player" />
          )}
        </div>
      </div>
    </div>
  );
}

export default function Library() {
  const { t } = useTranslation();
  const { admin } = useAuth();
  const [data, setData] = useState({ items: [], totalVideos: 0, totalDocs: 0 });
  const [batches, setBatches] = useState([]);
  const [loading, setLoad] = useState(true);
  const [modal, setModal] = useState({ show: false, item: null });
  const [theater, setTheater] = useState({ show: false, url: '', title: '', videoGuid: '' });
  const isAdmin = !!admin;
  const [bunnyStats, setBunnyStats] = useState(null);

  const fetchIdRef = React.useRef(0);

  const load = (isMounted = true) => {
    const currentId = ++fetchIdRef.current;
    Promise.all([
      libraryAPI.getAll(),
      batchesAPI.getAll(),
      isAdmin ? libraryAPI.getBunnyStats().catch(() => null) : Promise.resolve(null)
    ]).then(([libRes, batchRes, bunnyRes]) => {
      if (!isMounted || currentId !== fetchIdRef.current) return;
      const uniqueMap = new Map();
      (libRes.data.categories || []).flatMap(c => c.items || []).forEach(item => {
        const id = item._id || item.id;
        if (id) uniqueMap.set(id.toString(), item);
      });
      const uniqueItems = Array.from(uniqueMap.values());
      setData({
        items: uniqueItems,
        totalVideos: libRes.data.totalVideos,
        totalDocs: libRes.data.totalDocs
      });
      setBatches(batchRes.data);
      if (bunnyRes?.data) setBunnyStats(bunnyRes.data);
    }).finally(() => {
      if (isMounted && currentId === fetchIdRef.current) setLoad(false);
    });
  };

  useEffect(() => {
    let isMounted = true;
    load(isMounted);
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (itemId) => {
    if (!window.confirm('Delete this resource permanently?')) return;
    try {
      const idToUse = itemId?._id || itemId?.id || itemId;
      await libraryAPI.deleteItem(idToUse);
      load();
    } catch { alert('Delete failed.'); }
  };

  const review = async (itemId, action) => {
    try {
      await libraryAPI.reviewItem({ itemId, action });
      load();
    } catch { alert('Review failed.'); }
  };

  return (
    <Layout title={t('library.pageTitle', 'Resource Library')}>
      {modal.show && <ItemModal item={modal.item} batches={batches} onClose={() => setModal({ show: false, item: null })} onSave={load} />}
      {theater.show && <VideoTheaterModal url={theater.url} title={theater.title} videoGuid={theater.videoGuid} onClose={() => setTheater({ show: false, url: '', title: '', videoGuid: '' })} />}

      <div className="dash-page-header">
        <div>
          <h1 className="dash-page-title">{t('library.pageHeader', 'Resource Center')}</h1>
          <p className="text-sm text-muted">{t('library.pageSubtitle', 'A curated library of dance tutorials, manuals, and academy guidelines.')}</p>
        </div>
        {isAdmin && <button className="btn btn-primary" onClick={() => setModal({ show: true, item: null })}>{t('library.addNewResource', '+ Add New Resource')}</button>}
      </div>

      {isAdmin && bunnyStats && (
        <div className="dash-card mb-6" style={{ background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.08) 0%, rgba(255, 159, 67, 0.08) 100%)', border: '1px solid rgba(255, 107, 107, 0.2)', padding: '16px 24px', borderRadius: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 28 }}>🐰</div>
              <div>
                <h4 style={{ margin: 0, fontWeight: 700, fontSize: 16 }}>{t('library.bunnyTracker', 'Bunny.net Stream & Billing Tracker')}</h4>
                <p className="text-xs text-muted" style={{ margin: 0 }}>{t('library.bunnyTrackerSub', 'Live video streaming quota & credit tracking')}</p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
              <div>
                <div className="text-xs text-muted">{t('library.trialCreditsLeft', 'Trial Credits Left')}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#4cd137' }}>
                  ${(bunnyStats.trialBalance || 0).toFixed(2)} USD
                </div>
              </div>
              <div>
                <div className="text-xs text-muted">{t('library.accountBalance', 'Account Balance')}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#00d2d3' }}>
                  ${(bunnyStats.balance || 0).toFixed(2)} USD
                </div>
              </div>
              <div>
                <div className="text-xs text-muted">{t('library.bunnyStoredVideos', 'Bunny Stored Videos')}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#ff9f43' }}>
                  {bunnyStats.bunnyVideoCount} {t('library.videos', 'Videos')}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid-3 mb-8" style={{ gap:16 }}>
        <div className="dash-stat-card">
          <div className="dash-stat-label">{t('library.totalVideoLectures', 'Total Video Lectures')}</div>
          <div className="dash-stat-val" style={{ color: '#ff6b6b' }}>{data.totalVideos}</div>
        </div>
        <div className="dash-stat-card">
          <div className="dash-stat-label">{t('library.academicDocuments', 'Academic Documents')}</div>
          <div className="dash-stat-val" style={{ color: '#ffd93d' }}>{data.totalDocs}</div>
        </div>
        <div className="dash-stat-card">
          <div className="dash-stat-label">{t('library.totalResources', 'Total Resources')}</div>
          <div className="dash-stat-val" style={{ color: '#ffffff' }}>{data.items.length}</div>
        </div>
      </div>

      {loading ? (
        <div className="p-8">{t('library.loading', '⏳ Loading Library...')}</div>
      ) : (
        <div className="dash-card">
          <div className="grid-3 p-4" style={{ gap:24 }}>
            {data.items.length === 0 ? (
              <div className="col-span-3 text-center p-8 text-muted">{t('library.noItems', 'No resources uploaded yet.')}</div>
            ) : data.items.map((item, index) => {
              const canEdit = isAdmin || (admin?.role === 'tutor' && item.uploadedBy === admin.id);
              const isLocalFile = item.url?.startsWith('/uploads');
              const finalUrl = isLocalFile ? `${process.env.REACT_APP_API_URL?.replace('/api','') || 'http://localhost:5000'}${item.url}` : item.url;
              
              // Extract thumbnail for YouTube
              let thumbnailUrl = '';
              const isYoutube = item.url?.includes('youtube.com') || item.url?.includes('youtu.be');
              if (isYoutube) {
                const vId = item.url.includes('v=') ? item.url.split('v=')[1].split('&')[0] : item.url.split('/').pop();
                thumbnailUrl = `https://img.youtube.com/vi/${vId}/hqdefault.jpg`;
              }

              return (
                <div key={item.id || item._id || index} className="dash-card" style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  {/* Preview Section */}
                  <div style={{ height: 160, background: '#1a1a2e', position: 'relative', cursor: 'pointer' }}
                       onClick={() => { if(item.type === 'video') setTheater({ show: true, url: finalUrl, title: item.title, videoGuid: item.videoGuid }) }}>
                    {item.type === 'video' ? (
                      item.videoGuid ? (
                        <div style={{ width: '100%', height: '100%', backgroundImage: `url(https://${process.env.REACT_APP_BUNNY_CDN_HOSTNAME || 'vz-ae000630-21d.b-cdn.net'}/${item.videoGuid}/thumbnail.jpg)`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#e05252', fontSize: 24, paddingLeft: 4 }}>
                              ▶
                            </div>
                          </div>
                        </div>
                      ) : isYoutube ? (
                        <div style={{ width: '100%', height: '100%', backgroundImage: `url(${thumbnailUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#e05252', fontSize: 24, paddingLeft: 4 }}>
                              ▶
                            </div>
                          </div>
                        </div>
                      ) : (
                        <video src={finalUrl} style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.8 }} />
                      )
                    ) : item.type === 'image' ? (
                      <div style={{ width: '100%', height: '100%' }}>
                        <img src={finalUrl} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    ) : (
                      <div style={{ width: '100%', height: '100%', background: '#fff9e6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                        <span style={{ fontSize: 40, opacity: 0.2 }}>📄</span>
                        <span style={{ color: '#d4a017', fontWeight: 600, marginTop: 8, textAlign: 'center' }}>PDF / Document</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Info Section */}
                  <div style={{ padding: 16, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8, color: '#111' }}>{tField(item.title)}</div>
                    
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                      {item.batchIds && item.batchIds.length > 0 ? (
                        item.batchIds.map(bid => {
                          const bidStr = typeof bid === 'object' ? (bid._id || bid.id) : bid;
                          const batchFound = batches.find(b => (b._id || b.id) === bidStr);
                          const bNameObj = typeof bid === 'object' && bid.name ? bid.name : (batchFound?.name || 'Unknown Batch');
                          const bName = bNameObj?.en || bNameObj?.zh || bNameObj;
                          return <span key={bidStr} className="badge badge-outline" style={{ fontSize: 10, borderColor: 'var(--wine)', color: 'var(--wine)', background: 'rgba(123, 29, 58, 0.04)' }}>{typeof bName === 'string' ? bName : 'Unknown Batch'}</span>;
                        })
                      ) : (
                        <span className="badge badge-outline" style={{ fontSize: 10, borderColor: '#e69900', color: '#e69900' }}>{t('library.globalResource', 'Global Resource')}</span>
                      )}
                      
                      {item.subject && <span className="badge badge-outline" style={{ fontSize: 10, background: '#f5f5f5' }}>{tField(item.subject)}</span>}
                      
                      <span className={`badge ${item.status === 'approved' ? 'badge-success' : 'badge-danger'}`} style={{ fontSize: 10, textTransform: 'uppercase' }}>
                        {t(`library.status_${item.status}`, item.status)}
                      </span>
                    </div>

                    <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 12, borderTop: '1px solid #f0e6da' }}>
                      {item.type === 'video' ? (
                         <button 
                            className="text-xs text-blue btn-link" 
                            style={{ background:'none', border:'none', padding:0, cursor:'pointer', fontWeight: 700, alignSelf: 'flex-start' }}
                            onClick={() => setTheater({ show: true, url: finalUrl, title: item.title })}
                         >{t('library.playVideo', '▶ Play Video')}</button>
                       ) : item.type === 'image' ? (
                         <a href={finalUrl} target="_blank" rel="noreferrer" className="text-xs text-blue" style={{ fontWeight: 700, alignSelf: 'flex-start' }}>{t('library.viewImage', '👁 View Full Image')}</a>
                       ) : (
                         <a href={finalUrl} target="_blank" rel="noreferrer" className="text-xs text-blue" style={{ fontWeight: 700, alignSelf: 'flex-start' }}>{t('library.previewDoc', '👁 Preview Document')}</a>
                       )}

                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {isAdmin && item.status === 'pending' && (
                          <>
                            <button className="btn btn-xs btn-primary" onClick={() => review(item.id || item._id, 'approved')}>{t('library.approve', 'Approve')}</button>
                            <button className="btn btn-xs btn-outline" onClick={() => review(item.id || item._id, 'rejected')}>{t('library.reject', 'Reject')}</button>
                          </>
                        )}
                        {canEdit && <button className="btn btn-xs btn-outline" onClick={() => setModal({ show: true, item })}>{t('library.edit', 'Edit')}</button>}
                        {canEdit && <button className="btn btn-xs btn-outline text-danger" onClick={() => handleDelete(item.id || item._id)}>✕</button>}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Layout>
  );
}
